import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { ORDERS, LINES, lineEfficiency, pick } from "@/demo/data";

const RISK = { low: { k: "demoRiskLow", c: "var(--mint)" }, mid: { k: "demoRiskMid", c: "var(--cyan)" }, high: { k: "demoRiskHigh", c: "#ff9c6b" } };
const STAGES = ["demoStageCut", "demoStageSew", "demoStageFinish", "demoStagePack"];

function StageBars({ stages }) {
  const { t } = useLang();
  return (
    <div className="grid grid-cols-4 gap-2">
      {stages.map((v, i) => (
        <div key={i}>
          <div className="h-1.5 w-full overflow-hidden bg-[var(--navy-3)]">
            <motion.i className="block h-full origin-left bg-gradient-to-r from-[var(--mint)] to-[var(--cyan)]" initial={{ scaleX: 0 }} animate={{ scaleX: v / 100 }} transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }} />
          </div>
          <div className="mt-1.5 flex justify-between text-[0.62rem] text-[var(--muted-2)]">
            <span>{t(STAGES[i])}</span>
            <span className="mono">{v}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function BrandView() {
  const { t, lang } = useLang();
  const [sel, setSel] = useState(ORDERS[0].po);
  const order = ORDERS.find((o) => o.po === sel);
  const risk = RISK[order.risk];
  const overall = Math.round(order.stages.reduce((a, b) => a + b, 0) / order.stages.length);
  const lines = LINES.filter((l) => order.lines.includes(l.id));

  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]" data-testid="brand-view">
      <div className="surface overflow-hidden">
        <div className="flex items-center justify-between p-5">
          <span className="overline">{t("demoOrders")}</span>
          <span className="text-xs text-[var(--muted-2)]">
            {t("demoLastUpdate")} <span className="mono">14:20</span>
          </span>
        </div>
        <div className="hidden grid-cols-[6rem_1fr_5rem_6rem_6rem] gap-3 border-t border-line px-5 py-2 md:grid">
          {["demoOrder", "demoStyle", "demoQty", "demoShipDate", "demoRisk"].map((k) => (
            <span key={k} className="label">
              {t(k)}
            </span>
          ))}
        </div>
        {ORDERS.map((o) => {
          const active = o.po === sel;
          const r = RISK[o.risk];
          return (
            <button
              key={o.po}
              type="button"
              onClick={() => setSel(o.po)}
              aria-pressed={active}
              data-testid={`brand-order-${o.po}`}
              className={`grid w-full grid-cols-[6rem_1fr_6rem] items-center gap-3 border-t border-line px-5 py-4 text-left transition-colors duration-300 md:grid-cols-[6rem_1fr_5rem_6rem_6rem] ${active ? "bg-[var(--navy-2)]" : "hover:bg-[var(--navy-1)]"}`}
            >
              <span className="mono text-sm text-[var(--warm)]">{o.po}</span>
              <div className="min-w-0">
                <p className="truncate text-sm text-[var(--text)]">{o.style}</p>
                <p className="truncate text-xs text-[var(--muted-2)]">{pick(o.client, lang)}</p>
              </div>
              <span className="mono hidden text-xs text-[var(--muted)] md:block">{o.qty.toLocaleString()}</span>
              <span className="mono hidden text-xs text-[var(--muted)] md:block">{o.ship}</span>
              <span className="inline-flex items-center gap-2 text-xs" style={{ color: r.c }}>
                <i className="h-1.5 w-1.5 rounded-full" style={{ background: r.c }} />
                {t(r.k)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-6">
        <div className="surface p-6" data-testid="brand-order-detail">
          <div className="flex items-start justify-between">
            <div>
              <span className="label">{t("demoOrder")}</span>
              <p className="display display-md mt-2 text-[var(--warm)]">{order.po}</p>
              <p className="mt-1 text-sm text-[var(--muted)]">
                {order.style} · {order.qty.toLocaleString()} {t("demoUnits")}
              </p>
            </div>
            <div className="text-right">
              <span className="label">{t("demoProgress")}</span>
              <p className="mono mt-2 text-3xl font-semibold text-[var(--mint)]" data-testid="brand-progress">
                {overall}%
              </p>
            </div>
          </div>
          <div className="mt-6">
            <StageBars stages={order.stages} />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-px bg-[var(--line)]">
            <div className="bg-[var(--navy)] p-4">
              <span className="label">{t("demoShipDate")}</span>
              <p className="mono mt-1 text-lg text-[var(--warm)]">{order.ship}</p>
            </div>
            <div className="bg-[var(--navy)] p-4">
              <span className="label">{t("demoRisk")}</span>
              <p className="mt-1 text-lg font-medium" style={{ color: risk.c }} data-testid="brand-risk">
                {t(risk.k)}
              </p>
            </div>
          </div>
        </div>

        <div className="surface p-6" data-testid="brand-quality">
          <span className="overline text-[var(--cyan)]">{t("demoQuality")}</span>
          <div className="mt-4 grid grid-cols-3 gap-px bg-[var(--line)]">
            {[
              ["demoAqlPass", order.aql],
              ["demoInline", order.inline],
              ["demoFinal", order.final],
            ].map(([k, v]) => (
              <div key={k} className="bg-[var(--navy)] p-3">
                <span className="label !text-[0.58rem]">{t(k)}</span>
                <p className="mono mt-1 text-lg text-[var(--warm)]">{v}%</p>
              </div>
            ))}
          </div>
          <ul className="mt-4 divide-y divide-[var(--line)] border-t border-line">
            {lines.map((l) => (
              <li key={l.id} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-[var(--text)]">{l.name}</span>
                <span className="mono text-xs text-[var(--muted)]">
                  {t("demoEfficiency")} {lineEfficiency(l)}% · {t("demoDhu")} {l.dhu}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
